import React, { forwardRef } from 'react';
import classNames from 'classnames';
import ErrorMessage from './ErrorMessage';
import InfoMessage from './InfoMessage';

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	/** Rendered beside the label — a badge or hint that belongs to the field, not the value. */
	labelExtra?: React.ReactNode;
	error?: string;
	required?: boolean;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	tooltip?: string;
	/**
	 * Helper text under the field. Rendered inside the field's own wrapper so it picks up the same
	 * spacing as the label and error — a caller passing its own `<InfoMessage>` as a sibling sits
	 * outside that wrapper and ends up flush against the input.
	 */
	description?: React.ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
	(
		{ label, labelExtra, error, required, className, id, startAdornment, endAdornment, tooltip, description, ...props },
		ref,
	) => {
		const inputId = id || label?.replace(/\s+/g, '-').toLowerCase();

		return (
			<div className="flex flex-col gap-1 w-full">
				{label && (
					<div className="flex items-center gap-1">
						<label htmlFor={inputId} className="text-sm text-gray-800">
							{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
						</label>
						{labelExtra}
						{tooltip && (
							<div className="relative group">
								<span className="material-symbols-outlined text-gray-400 text-sm leading-none cursor-help">
									help
								</span>
								<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-10 pointer-events-none">
									{tooltip}
								</div>
							</div>
						)}
					</div>
				)}
				<div className="flex relative items-center custom-input">
					{startAdornment && (
						<div className="flex absolute left-3 items-center text-gray-500 pointer-events-none">
							{startAdornment}
						</div>
					)}
					<input
						id={inputId}
						ref={ref}
						className={classNames(
							'px-3 py-3 border rounded-md outline-none transition-all w-full',
							'placeholder:text-gray-400 text-sm',
							// eslint-disable-next-line no-nested-ternary
							props.disabled
								? 'border-gray-300 bg-gray-100 cursor-not-allowed text-gray-500 placeholder:text-gray-300'
								: error
									? 'border-red-400 bg-red-50 text-red-900 focus:border-red-400 focus:ring-1 focus:ring-[#ef4444]'
									: 'border-gray-300 hover:border-gray-400 focus:border-[var(--colour-brand-primary)] focus:ring-1 focus:ring-[var(--colour-brand-primary)]',
							props.readOnly && 'bg-gray-50 text-gray-600',
							startAdornment && 'pl-8',
							endAdornment && 'pr-10',
							className,
						)}
						{...props}
					/>
					{endAdornment && (
						<div className="flex absolute right-3 items-center text-gray-500 pointer-events-none">
							{endAdornment}
						</div>
					)}
				</div>
				{/* mt-1.5 on top of the wrapper's gap-1 — 4px reads as touching the input. */}
				{description && <InfoMessage className="mt-1.5">{description}</InfoMessage>}
				<ErrorMessage error={error} />
			</div>
		);
	},
);
